import { useMutation } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';

import { COMPLETE_LOGIN_KEY, completeLoginFromCallback } from '../../utils/api';
import type { IOAuth2Config } from '../../utils/types';
import { useMessage } from '../../../hooks/useMessage';

interface IUseCompleteLoginProps {
  config: IOAuth2Config;
  axiosInstance: AxiosInstance;
  setToken: (token: string | undefined) => void;
  onError?: (error: unknown) => void;
}

const useCompleteLogin = ({
  config,
  axiosInstance,
  setToken,
  onError,
}: IUseCompleteLoginProps) => {
  const message = useMessage();

  const mutation = useMutation({
    mutationKey: [COMPLETE_LOGIN_KEY],
    mutationFn: () =>
      completeLoginFromCallback(config, axiosInstance, setToken),
    retry: 1,
    retryDelay: 1000,
    onError: (error: unknown) => {
      if (onError) {
        onError(error);
      } else {
        const errMsg = error instanceof Error ? error.message : 'Login failed.';
        message.error(errMsg);
        console.error('Complete login error:', error);
      }
    },
  });

  return {
    completeLogin: mutation.mutate,
    completeLoginAsync: mutation.mutateAsync,
    isCompletingLogin: mutation.isPending,
    completeLoginError: mutation.error,
    data: mutation.data,
    status: mutation.status,
  };
};

export default useCompleteLogin;
